import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * QA-536 주문 목록 노출 항목 설정 — 상품 목록 [노출 항목](useProductColumnPrefs)과 같은 방식.
 * 운영자 개인 화면 설정이라 localStorage 에만 둔다(BE 저장 없음). 주문번호(+선택 체크)는 항상 보인다.
 * 주문별·상품주문번호별 두 목록이 같은 설정을 쓴다.
 */
export type ToggleableOrderColumn =
  | 'orderedAt'
  | 'mall'
  | 'paymentMethod'
  | 'paymentAmount'
  | 'ordererName'
  | 'ordererEmail'
  | 'ordererMobile'
  | 'product'
  | 'quantity'
  | 'selfProductCode'
  | 'unitPrice'
  | 'totalDiscountAmount'
  | 'totalOrderAmount'
  | 'shippingFee'
  | 'payableAmount'
  | 'fulfillmentType'
  | 'status';

/** 표시 순서 정본 — 헤더·셀·메뉴가 이 순서를 따른다. */
export const TOGGLEABLE_ORDER_COLUMNS: ReadonlyArray<ToggleableOrderColumn> = [
  'orderedAt',
  'mall',
  'paymentMethod',
  'paymentAmount',
  'ordererName',
  'ordererEmail',
  'ordererMobile',
  'product',
  'quantity',
  'selfProductCode',
  'unitPrice',
  'totalDiscountAmount',
  'totalOrderAmount',
  'shippingFee',
  'payableAmount',
  'fulfillmentType',
  'status',
];

/** 메뉴·헤더 라벨(운영 요청 용어). */
export const ORDER_COLUMN_LABELS: Record<ToggleableOrderColumn, string> = {
  orderedAt: '주문일시',
  mall: '국가몰',
  paymentMethod: '결제수단',
  paymentAmount: '결제금액',
  ordererName: '주문자명',
  ordererEmail: '주문자 이메일',
  ordererMobile: '주문자 연락처',
  product: '상품명',
  quantity: '상품수량',
  selfProductCode: '자체상품코드',
  unitPrice: '판매가',
  totalDiscountAmount: '총 할인금액',
  totalOrderAmount: '총 주문금액',
  shippingFee: '총 배송비',
  payableAmount: '실 결제금액',
  fulfillmentType: '이행 유형',
  status: '주문상태',
};

/** 저장값이 없을 때 — 지금까지 보이던 칸(주문일시·주문자·상품·국가몰·결제금액·배송비·상태·이행)을 그대로. */
export const DEFAULT_VISIBLE_ORDER_COLUMNS: ReadonlyArray<ToggleableOrderColumn> = [
  'orderedAt',
  'mall',
  'paymentAmount',
  'ordererName',
  'ordererEmail',
  'product',
  'quantity',
  'unitPrice',
  'shippingFee',
  'fulfillmentType',
  'status',
];

const STORAGE_KEY = 'order-column-prefs';

const isToggleable = (value: unknown): value is ToggleableOrderColumn =>
  typeof value === 'string' && (TOGGLEABLE_ORDER_COLUMNS as ReadonlyArray<string>).includes(value);

const readStored = (): ReadonlyArray<ToggleableOrderColumn> => {
  if (typeof window === 'undefined') return DEFAULT_VISIBLE_ORDER_COLUMNS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_ORDER_COLUMNS;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_ORDER_COLUMNS;
    const known = parsed.filter(isToggleable);
    return TOGGLEABLE_ORDER_COLUMNS.filter((c) => known.includes(c));
  } catch {
    return DEFAULT_VISIBLE_ORDER_COLUMNS;
  }
};

const persist = (columns: ReadonlyArray<ToggleableOrderColumn>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // localStorage 불가 — 화면 동작만 유지.
  }
};

export interface OrderColumnPrefsState {
  visibleColumns: ReadonlyArray<ToggleableOrderColumn>;
  /** 단건 토글 — 마지막 1개는 끌 수 없다. */
  toggleColumn: (column: ToggleableOrderColumn) => void;
  resetColumns: () => void;
}

export const useOrderColumnPrefs = create<OrderColumnPrefsState>()(
  devtools(
    (set) => ({
      visibleColumns: readStored(),
      toggleColumn: (column) =>
        set(
          (s) => {
            const has = s.visibleColumns.includes(column);
            if (has && s.visibleColumns.length <= 1) return s;
            const next = has
              ? s.visibleColumns.filter((c) => c !== column)
              : TOGGLEABLE_ORDER_COLUMNS.filter((c) => c === column || s.visibleColumns.includes(c));
            persist(next);
            return { visibleColumns: next };
          },
          false,
          'toggleColumn',
        ),
      resetColumns: () =>
        set(
          () => {
            persist(DEFAULT_VISIBLE_ORDER_COLUMNS);
            return { visibleColumns: DEFAULT_VISIBLE_ORDER_COLUMNS };
          },
          false,
          'resetColumns',
        ),
    }),
    { name: 'order-column-prefs' },
  ),
);
